import React, { useState, useEffect } from 'react';
import { MdPerson, MdWork, MdCheckCircle, MdEmail, MdBlock } from 'react-icons/md';
import { FiPackage, FiCalendar, FiClock } from 'react-icons/fi';
import { User } from '../types';
import { apiService } from '../services/api';

interface UserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: User;
}

interface UserStats {
  total_jobs: number;
  total_cvs_uploaded: number;
  total_cvs_analyzed: number;
  total_runs: number;
  runs_by_status: {
    completed: number;
    failed: number;
    running: number;
  };
  run_stats: {
    total_successful_cvs: number;
    total_failed_cvs: number;
  };
  recent_runs: Array<{
    id: number;
    job__title: string;
    status: string;
    total_cvs: number;
    successful_cvs: number;
    failed_cvs: number;
    created_at: string;
    completed_at: string | null;
  }>;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ isOpen, onClose, user }) => {
  const [stats, setStats] = useState<UserStats>({
    total_jobs: 0,
    total_cvs_uploaded: 0,
    total_cvs_analyzed: 0,
    total_runs: 0,
    runs_by_status: { completed: 0, failed: 0, running: 0 },
    run_stats: { total_successful_cvs: 0, total_failed_cvs: 0 },
    recent_runs: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && user) {
      fetchUserStats();
    }
  }, [isOpen, user]);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const response = await apiService.getUserStats(user.id);
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching user stats:', error);
      setStats({
        total_jobs: 0,
        total_cvs_uploaded: 0,
        total_cvs_analyzed: 0,
        total_runs: 0,
        runs_by_status: { completed: 0, failed: 0, running: 0 },
        run_stats: { total_successful_cvs: 0, total_failed_cvs: 0 },
        recent_runs: [],
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string | null | undefined) => {
    if (!date) return 'Never';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Invalid date';
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getUsagePercentage = (used: number, limit: number) => {
    if (limit === 0 || limit === -1) return 0;
    return Math.min((used / limit) * 100, 100);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl dark:bg-navy-800 my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="border-b border-gray-200 p-4 md:p-6 dark:border-white/10">
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="flex h-12 w-12 md:h-16 md:w-16 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-brand-500 to-purple-600 text-lg md:text-2xl font-bold text-white">
                {user.first_name[0]}
                {user.last_name[0]}
              </div>
              <div className="min-w-0">
                <h3 className="text-lg md:text-2xl font-bold text-navy-700 dark:text-white truncate">
                  {user.first_name} {user.last_name}
                </h3>
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 truncate">{user.email}</p>
                <div className="mt-2 flex gap-2">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      user.role === 'admin'
                        ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/20 dark:text-purple-400'
                        : 'bg-blue-100 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                    }`}
                  >
                    {user.role}
                  </span>
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${
                      user.is_enabled
                        ? 'bg-green-100 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                        : 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                    }`}
                  >
                    {user.is_enabled ? 'Active' : 'Disabled'}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 dark:hover:bg-navy-700 text-xl md:text-2xl font-bold"
            >
              ×
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 md:p-6 space-y-5">
          {/* Account Status Section */}
          <div className="rounded-xl bg-gray-50 p-4 dark:bg-navy-700">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-gray-700 dark:text-gray-300">
              <MdPerson className="h-4 w-4" />
              ACCOUNT STATUS
            </h4>
            <div className="flex flex-wrap gap-2">
              <div className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                user.is_email_confirmed ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              }`}>
                {user.is_email_confirmed ? '✓' : '⚠'} Email {user.is_email_confirmed ? 'Verified' : 'Unverified'}
              </div>
              <div className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold ${
                user.is_profile_complete ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
              }`}>
                {user.is_profile_complete ? '✓' : '○'} Profile {user.is_profile_complete ? 'Complete' : 'Incomplete'}
              </div>
              <div className="inline-flex items-center gap-1 rounded-lg bg-purple-100 px-3 py-1.5 text-xs font-semibold text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                🆔 ID: {user.id}
              </div>
            </div>
          </div>

          {/* Timestamps Section */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl bg-gradient-to-br from-blue-50 to-blue-100 p-4 dark:from-blue-900/20 dark:to-blue-900/30">
              <div className="flex items-center gap-2 mb-2">
                <FiCalendar className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                <p className="text-xs font-bold text-blue-700 dark:text-blue-400">CREATED</p>
              </div>
              <p className="text-sm font-semibold text-navy-700 dark:text-white">{formatDate(user.created_at)}</p>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-purple-50 to-purple-100 p-4 dark:from-purple-900/20 dark:to-purple-900/30">
              <div className="flex items-center gap-2 mb-2">
                <FiClock className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                <p className="text-xs font-bold text-purple-700 dark:text-purple-400">LAST LOGIN</p>
              </div>
              <p className="text-sm font-semibold text-navy-700 dark:text-white">{formatDate(user.last_login)}</p>
            </div>
            <div className="rounded-xl bg-gradient-to-br from-green-50 to-green-100 p-4 dark:from-green-900/20 dark:to-green-900/30">
              <div className="flex items-center gap-2 mb-2">
                <FiCalendar className="h-4 w-4 text-green-600 dark:text-green-400" />
                <p className="text-xs font-bold text-green-700 dark:text-green-400">UPDATED</p>
              </div>
              <p className="text-sm font-semibold text-navy-700 dark:text-white">{formatDate(user.updated_at)}</p>
            </div>
          </div>

          {/* User Information */}
          <div className="rounded-xl border-2 border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-900/30 dark:bg-indigo-900/10">
            <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-indigo-800 dark:text-indigo-300">
              <MdPerson className="h-4 w-4" />
              USER INFORMATION
            </h4>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Full Name</p>
                <p className="mt-1 text-base font-bold text-navy-700 dark:text-white">
                  {user.first_name} {user.last_name}
                </p>
              </div>
              <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Email Address</p>
                <p className="mt-1 text-sm font-semibold text-navy-700 dark:text-white break-all">
                  {user.email}
                </p>
              </div>
              <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Organization</p>
                <p className="mt-1 text-base font-bold text-navy-700 dark:text-white">
                  {user.organization || 'No organization'}
                </p>
              </div>
              <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Role</p>
                <p className="mt-1 text-base font-bold text-navy-700 dark:text-white capitalize">
                  {user.role === 'admin' ? '👑 Admin' : '👤 User'}
                </p>
              </div>
              <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Promo Code Used</p>
                <p className="mt-1 text-base font-bold text-navy-700 dark:text-white">
                  {user.promo_code_used ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-100 px-2 py-1 text-sm font-semibold text-brand-700 dark:bg-brand-900/30 dark:text-brand-400">
                      🎟️ {user.promo_code_used}
                    </span>
                  ) : (
                    <span className="text-gray-400">No promo code</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Package & Usage */}
          {user.package && (
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4 dark:border-blue-900/30 dark:bg-blue-900/10">
              <div className="mb-3 flex items-center gap-2">
                <FiPackage className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                <h4 className="text-sm font-bold text-blue-800 dark:text-blue-300">PACKAGE & RESOURCE USAGE</h4>
              </div>
              
              <div className="mb-3 rounded-lg bg-white p-3 dark:bg-navy-800">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">Current Package</p>
                <p className="mt-1 text-lg font-bold text-navy-700 dark:text-white">{user.package.title}</p>
              </div>

              <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                {/* Monthly AI */}
                <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">🤖 MONTHLY AI</p>
                    {user.custom_monthly_ai_limit && <span className="text-xs text-purple-600 dark:text-purple-400" title="Custom limit">⚙️</span>}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <p className="text-xl font-bold text-navy-700 dark:text-white">{user.current_month_ai_usage || 0}</p>
                    <p className="text-xs text-gray-500">/ {user.custom_monthly_ai_limit ?? user.package.monthly_limit_transaction_ai}</p>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-700">
                    <div className={`h-full transition-all ${
                      getUsagePercentage(user.current_month_ai_usage, user.custom_monthly_ai_limit ?? user.package.monthly_limit_transaction_ai) >= 80 ? 'bg-red-500' : 'bg-green-500'
                    }`} style={{ width: `${getUsagePercentage(user.current_month_ai_usage, user.custom_monthly_ai_limit ?? user.package.monthly_limit_transaction_ai)}%` }} />
                  </div>
                </div>

                {/* Total AI */}
                <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">🔮 TOTAL AI</p>
                    {user.custom_total_ai_limit && <span className="text-xs text-purple-600 dark:text-purple-400" title="Custom limit">⚙️</span>}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <p className="text-xl font-bold text-navy-700 dark:text-white">{user.total_ai_usage || 0}</p>
                    <p className="text-xs text-gray-500">/ {user.custom_total_ai_limit ?? user.package.total_transaction_ai}</p>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-700">
                    <div className={`h-full transition-all ${
                      getUsagePercentage(user.total_ai_usage, user.custom_total_ai_limit ?? user.package.total_transaction_ai) >= 80 ? 'bg-red-500' : 'bg-green-500'
                    }`} style={{ width: `${getUsagePercentage(user.total_ai_usage, user.custom_total_ai_limit ?? user.package.total_transaction_ai)}%` }} />
                  </div>
                </div>

                {/* Storage */}
                <div className="rounded-lg bg-white p-3 dark:bg-navy-800">
                  <div className="mb-2 flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">💾 STORAGE</p>
                    {user.custom_storage_limit && <span className="text-xs text-purple-600 dark:text-purple-400" title="Custom limit">⚙️</span>}
                  </div>
                  <div className="flex items-baseline gap-1">
                    <p className="text-xl font-bold text-navy-700 dark:text-white">{user.stats?.storage_used_mb || 0}</p>
                    <p className="text-xs text-gray-500">MB / {user.custom_storage_limit ?? user.package.storage_mo}MB</p>
                  </div>
                  <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-navy-700">
                    <div className={`h-full transition-all ${
                      getUsagePercentage(user.stats?.storage_used_mb || 0, user.custom_storage_limit ?? user.package.storage_mo) >= 80 ? 'bg-red-500' : 'bg-green-500'
                    }`} style={{ width: `${getUsagePercentage(user.stats?.storage_used_mb || 0, user.custom_storage_limit ?? user.package.storage_mo)}%` }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Section */}
          <div>
            <h4 className="mb-4 flex items-center gap-2 text-lg font-bold text-navy-700 dark:text-white">
              <MdWork className="h-5 w-5 text-brand-500" />
              Activity Statistics
            </h4>
            
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
              </div>
            ) : (
              <>
                {/* Main Stats Grid */}
                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {/* Total Jobs */}
                  <div className="rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 p-4 text-white shadow-lg">
                    <MdWork className="h-6 w-6 opacity-80" />
                    <p className="mt-2 text-xs opacity-90">Jobs</p>
                    <p className="text-2xl font-bold">{stats.total_jobs}</p>
                  </div>

                  {/* Total CVs Uploaded */}
                  <div className="rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 p-4 text-white shadow-lg">
                    <MdWork className="h-6 w-6 opacity-80" />
                    <p className="mt-2 text-xs opacity-90">CVs</p>
                    <p className="text-2xl font-bold">{stats.total_cvs_uploaded}</p>
                  </div>

                  {/* Total Runs */}
                  <div className="rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 p-4 text-white shadow-lg">
                    <MdCheckCircle className="h-6 w-6 opacity-80" />
                    <p className="mt-2 text-xs opacity-90">Runs</p>
                    <p className="text-2xl font-bold">{stats.total_runs}</p>
                  </div>

                  {/* CVs Analyzed */}
                  <div className="rounded-xl bg-gradient-to-br from-green-500 to-green-600 p-4 text-white shadow-lg">
                    <MdCheckCircle className="h-6 w-6 opacity-80" />
                    <p className="mt-2 text-xs opacity-90">Analyzed</p>
                    <p className="text-2xl font-bold">{stats.total_cvs_analyzed}</p>
                  </div>
                </div>

                {/* Analysis Runs Summary */}
                {stats.total_runs > 0 && (
                  <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700">
                    <h5 className="mb-3 text-sm font-bold text-gray-700 dark:text-gray-300">Analysis Runs Summary</h5>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-lg bg-green-100 p-3 text-center dark:bg-green-900/20">
                        <p className="text-2xl font-bold text-green-700 dark:text-green-400">{stats.runs_by_status.completed}</p>
                        <p className="text-xs text-green-600 dark:text-green-500">Completed</p>
                      </div>
                      <div className="rounded-lg bg-blue-100 p-3 text-center dark:bg-blue-900/20">
                        <p className="text-2xl font-bold text-blue-700 dark:text-blue-400">{stats.runs_by_status.running}</p>
                        <p className="text-xs text-blue-600 dark:text-blue-500">Running</p>
                      </div>
                      <div className="rounded-lg bg-red-100 p-3 text-center dark:bg-red-900/20">
                        <p className="text-2xl font-bold text-red-700 dark:text-red-400">{stats.runs_by_status.failed}</p>
                        <p className="text-xs text-red-600 dark:text-red-500">Failed</p>
                      </div>
                    </div>
                    
                    {/* CV Processing Stats */}
                    <div className="mt-3 flex items-center justify-between rounded-lg bg-white p-3 dark:bg-navy-800">
                      <div className="text-center">
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats.run_stats.total_successful_cvs}</p>
                        <p className="text-xs text-gray-500">Successful CVs</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-red-600 dark:text-red-400">{stats.run_stats.total_failed_cvs}</p>
                        <p className="text-xs text-gray-500">Failed CVs</p>
                      </div>
                      <div className="text-center">
                        <p className="text-lg font-bold text-navy-700 dark:text-white">
                          {stats.run_stats.total_successful_cvs + stats.run_stats.total_failed_cvs > 0
                            ? Math.round((stats.run_stats.total_successful_cvs / (stats.run_stats.total_successful_cvs + stats.run_stats.total_failed_cvs)) * 100)
                            : 0}%
                        </p>
                        <p className="text-xs text-gray-500">Success Rate</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Recent Runs Table */}
                {stats.recent_runs.length > 0 && (
                  <div className="mt-4">
                    <h5 className="mb-3 text-sm font-bold text-gray-700 dark:text-gray-300">Recent Analysis Runs</h5>
                    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-navy-600">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-navy-700">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400">Job</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-400">Status</th>
                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 dark:text-gray-400">CVs</th>
                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600 dark:text-gray-400">Date</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-navy-600">
                          {stats.recent_runs.map((run) => (
                            <tr key={run.id} className="bg-white dark:bg-navy-800">
                              <td className="px-3 py-2 text-navy-700 dark:text-white">
                                {run.job__title?.substring(0, 25) || `Run #${run.id}`}
                                {run.job__title && run.job__title.length > 25 && '...'}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                  run.status === 'completed' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400' :
                                  run.status === 'running' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400' :
                                  'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                                }`}>
                                  {run.status}
                                </span>
                              </td>
                              <td className="px-3 py-2 text-center text-gray-600 dark:text-gray-400">
                                <span className="text-green-600 dark:text-green-400">{run.successful_cvs}</span>
                                <span className="mx-1">/</span>
                                <span>{run.total_cvs}</span>
                              </td>
                              <td className="px-3 py-2 text-right text-xs text-gray-500 dark:text-gray-400">
                                {new Date(run.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 md:p-6 dark:border-white/10">
          <button
            onClick={onClose}
            className="w-full rounded-lg bg-gray-200 px-4 py-2.5 text-sm md:text-base font-medium text-gray-700 transition hover:bg-gray-300 dark:bg-navy-700 dark:text-white dark:hover:bg-navy-600"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
